import { usePathname } from "next/navigation";
import useTranslation from "next-translate/useTranslation";
import {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useRef,
  useState,
} from "react";

import ArrowLeftIcon from "@/assets/icons/arrow-left.svg";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { FormTypes } from "@/types/form";
import {
  GenerateJsonFile,
  ImportFromJsonFile,
  handleScroll,
} from "@/utilities/form-utils";

type Props = {
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  formDataArray: FormTypes[][];
  setFormDataArray: Dispatch<SetStateAction<FormTypes[][]>>;
  fortroendemodellen?: boolean;
  scrollRef: RefObject<HTMLSpanElement>;
};

export const FormBottomNav: FC<Props> = ({
  fortroendemodellen,
  setPage,
  page,
  formDataArray,
  setFormDataArray,
  scrollRef,
}) => {
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const pathname = usePathname();

  const clearForm = () => {
    //Clear the "Value" field on all objects that has the Value field.
    const tmpArr = formDataArray.map((item) => {
      item.forEach((data) => {
        if ("value" in data) {
          data.value = "";
        }
        if ("choices" in data) {
          data.selected = data.choices[data.choices.length - 1];
          data.value = "";
        }
      });
      return item;
    });

    setFormDataArray(tmpArr);
    localStorage.removeItem(`${pathname}Data`);
  };

  return (
    <nav
      className="z-40 space-y-lg pt-xl md:space-y-xl"
      aria-label={t("common|menu-form-bottom")}
    >
      <div className={`flex ${page === 1 ? "justify-end" : "justify-between"}`}>
        {page > 1 && (
          <Button
            label={t("pages|form$previous")}
            icon={ArrowLeftIcon}
            iconPosition="left"
            variant={"secondary"}
            onClick={() => {
              setPage(page - 1);
              handleScroll(scrollRef);
            }}
          />
        )}
        <Button
          label={t("pages|form$next")}
          icon={ArrowRightIcon}
          variant={"secondary"}
          iconPosition="right"
          onClick={() => {
            setPage(page + 1);
            handleScroll(scrollRef);
          }}
        />
      </div>

      {!fortroendemodellen && (
        <div className="flex flex-col gap-md md:flex-row md:justify-between">
          <Button
            label={t("pages|form$save-form")}
            onClick={() => setSaveModalOpen(true)}
            className="button--large w-full justify-center md:w-auto md:justify-start"
          />
          <input
            type="file"
            accept="application/json"
            title="Ladda upp JSON"
            ref={fileInputRef}
            onChange={(e) => {
              ImportFromJsonFile(e, formDataArray, setFormDataArray);
            }}
            className="hidden"
          />
          <Button
            label={t("pages|form$upload-json-file")}
            onClick={(e) => {
              e.preventDefault();
              fileInputRef.current?.click();
            }}
            className="button--large w-full justify-center md:w-auto md:justify-start"
          />
          <Button
            label={t("pages|form$clear-all-text")}
            onClick={() => setClearModalOpen(true)}
            className="button--large w-full justify-center md:w-auto md:justify-start"
          />
        </div>
      )}

      <Modal
        heading={t("pages|form$clear-confirm-text")}
        onClick={clearForm}
        modalOpen={clearModalOpen}
        setModalOpen={setClearModalOpen}
        closeBtn={"Avbryt"}
        confirmBtn={t("common|yes")}
      />

      <Modal
        heading={` När du klickar på OK så kommer en JSON-fil att genereras och laddas
          ner.`}
        text={`Läs i Guiden till förtroendemoellen på förstasidan om hur du använder
          JSON-filen.`}
        onClick={() => GenerateJsonFile(formDataArray)}
        modalOpen={saveModalOpen}
        setModalOpen={setSaveModalOpen}
        closeBtn={"Avbryt"}
        confirmBtn={"OK"}
      />
    </nav>
  );
};
